import { Date, LoadingState, View, DefinitionItem, DefinitionList, Row, Col } from "components/Shared";
import { translate } from "lib";
import Datasource, { DatasourceResponse } from "lib/datasource";
import AreaMeasurements from "components/Tenancies/AreaMeasurements";
import _ from "lodash";
import React, { memo, useEffect, useState } from "react";
// import { Button } from "v2/components/shared";

interface Props {
	tenancyId: string | number;
	editable?: boolean;
	name?: string;
	icon?: string;
	onClear?: any;
	passArea?: any;
}

// Renders a single tenancy's details only. The shared property + map panel used
// to live here too, which duplicated the map once per tenancy; it now lives in
// PropertyPanel and is rendered once by TenanciesTab.
const TenancyTab = ({ tenancyId, onClear }: Props) => {
	// Collapsed by default — see the heading below.
	const [open, setOpen] = useState(false);
	const [tenancy, setTenancy] = useState([] as any[]);
	const [tenancyDatasource] = useState(new Datasource({ mainModelName: "tenancy" }));

	useEffect(() => {
		if (_.isNil(tenancyId)) return;
		let sub = tenancyDatasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setTenancy(response.normalizedMainModelResponse)
		);
		return () => sub.unsubscribe();
	}, [tenancyDatasource, tenancyId]);

	/******************************************  API Call  ******************************************/
	useEffect(() => {
		if (!_.isEmpty(tenancyId)) tenancyDatasource.get("v2", `tenancies/${tenancyId}`, {});
	}, [tenancyDatasource, tenancyId]);

	if (_.isEmpty(tenancy)) return <LoadingState />;

	const areaMeasurements = _.get(tenancy, "area_measurements", []) as tcpinpoint.AreaMeasurement[];

	return (
		<View>
			{!!onClear && (
				<button type="button" className="close" aria-label="Close" onClick={onClear}>
					<span aria-hidden="true">×</span>
				</button>
			)}
			{/* Collapsed by default: a project can carry a dozen units and each one
			    now lists ~18 Yardi fields, so expanded-by-default buried the list.
			    Clicking the heading toggles it (keyboard-accessible via Enter/Space). */}
			<div
				role="button"
				tabIndex={0}
				aria-expanded={open}
				onClick={() => setOpen(previous => !previous)}
				onKeyPress={event => {
					if (event.key === "Enter" || event.key === " ") setOpen(previous => !previous);
				}}
				style={{ cursor: "pointer", userSelect: "none" }}
			>
				<h3 style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: 0 }}>
					<span
						aria-hidden="true"
						style={{
							display: "inline-block",
							transition: "transform 0.15s ease",
							transform: open ? "rotate(90deg)" : "none",
							fontSize: "0.6em",
						}}
					>
						&#9654;
					</span>
					{translate(_.get(tenancy, "name"))}
				</h3>
				<h4>{_.get(tenancy, "building_name")}</h4>
			</div>
			{/* Two columns once expanded: the unit's fields on the left, the dated
			    area series on the right, in the space the definition list leaves
			    empty. AreaMeasurements renders nothing when the unit has none, so
			    that column simply collapses. */}
			{open && (
			<Row>
			{/* Not an even split: the definition list is one narrow label-over-value
			    stack, while the table carries five columns and wraps its headers over
			    three lines at half width. The ratio is set INLINE rather than with
			    `col-4`/`col-8`, because scss/_common.scss declares `.row .col { flex: 1 }`
			    and loads after the Bootstrap CDN — it beats the grid classes on both
			    order and specificity, so they do nothing here. A unit with no
			    measurements has no second column and the fields take the whole row. */}
			<Col>
			<DefinitionList>
				{/* Every Yardi unit field, in payload order (see how-does-api-works.md
				    — /unit_data and Units[] on /lease_data). DefinitionItem renders
				    nothing for a blank value, so units Yardi hasn't populated stay
				    clean and only the fields that carry data appear. */}
				<DefinitionItem title="unit_code" value={_.get(tenancy, "unit_code")} />
				{/* `unit_type` rather than the shared `kind` label key, which Lease/
				    Project/Template/Inspection/Company also use. */}
				<DefinitionItem title="unit_type" value={translate(_.get(tenancy, "kind"))} />
				<DefinitionItem title="location" value={_.get(tenancy, "location")} />
				<DefinitionItem title="net_lettable_area" value={_.get(tenancy, "area")} suffix="m²" />
				<DefinitionItem title="lease_area_sqm" value={_.get(tenancy, "lease_area_sqm")} suffix="m²" />
				<DefinitionItem title="actual_area_sqm" value={_.get(tenancy, "actual_area_sqm")} suffix="m²" />
				<DefinitionItem title="actual_area_sqft" value={_.get(tenancy, "actual_area_sqft")} suffix="ft²" />
				<DefinitionItem title="lease_area_sqft" value={_.get(tenancy, "lease_area_sqft")} suffix="ft²" />
				<DefinitionItem title="area_id" value={_.get(tenancy, "area_id")} />
				<DefinitionItem title="country" value={_.get(tenancy, "country")} />
				<DefinitionItem title="address" value={_.get(tenancy, "address")} />
				{/* Only shown when true — `exclude: false` is the normal state and
				    would otherwise add a row to every unit. */}
				<DefinitionItem title="exclude" value={_.get(tenancy, "exclude") ? translate("yes") : false} />
				<DefinitionItem title="zone" value={_.get(tenancy, "zone")} />
				<DefinitionItem title="sub_zone" value={_.get(tenancy, "sub_zone")} />
				<DefinitionItem title="portfolio" value={_.get(tenancy, "portfolio")} />
				<DefinitionItem
					title="as_of_date"
					value={_.get(tenancy, "as_of_date") ? <Date date={_.get(tenancy, "as_of_date")} showTime={false} /> : false}
				/>
				{/* unit_status is lease-level and lives on Lease — see LeaseTab. */}
				<DefinitionItem title="removed_flag" value={_.get(tenancy, "removed_flag")} />
				<DefinitionItem title="relocation_status" value={_.get(tenancy, "relocation_status")} />
				{/* Yardi's own occupancy status for the unit, off /unit_data — not the
				    folded `occupancy_status`. */}
				<DefinitionItem title="unit_occ_status" value={_.get(tenancy, "unit_occ_status")} />
				<DefinitionItem title="division" value={_.get(tenancy, "division")} />
				<DefinitionItem title="precinct" value={_.get(tenancy, "precinct")} />
			</DefinitionList>
			</Col>
			{/* The series behind the four area rows opposite — those show only the
			    measurement in force TODAY, so without this a future-dated restatement
			    would be invisible. Shared with the tenancy edit page. */}
			{!_.isEmpty(areaMeasurements) && (
				<Col style={{ flex: 2 }}>
					<AreaMeasurements measurements={areaMeasurements} />
				</Col>
			)}
			</Row>
			)}
			{/* TODO - give user the ability to change which tenancy their project belongs to IN THE LEASE CARD*/}
		</View>
	);
};

export default memo(TenancyTab);
